import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className }) => {
  return (
    <div className={`markdown-body ${className || ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
              {...props}
            >
              {children}
            </a>
          ),
          pre: ({ children, ...props }) => (
            <div className="relative group">
              <CopyButton code={extractCodeFromPre(children)} />
              <pre className="bg-bg-elevated rounded-lg p-4 overflow-x-auto text-sm" {...props}>
                {children}
              </pre>
            </div>
          ),
          code: ({ className: codeClassName, children, ...props }) => {
            const isInline = !codeClassName;
            if (isInline) {
              return (
                <code className="bg-bg-elevated px-1.5 py-0.5 rounded text-sm text-primary" {...props}>
                  {children}
                </code>
              );
            }
            return <code className={codeClassName} {...props}>{children}</code>;
          },
          h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-50 mt-6 mb-3">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-bold text-gray-50 mt-5 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-semibold text-gray-50 mt-4 mb-2">{children}</h3>,
          h4: ({ children }) => <h4 className="text-base font-semibold text-gray-50 mt-3 mb-1">{children}</h4>,
          h5: ({ children }) => <h5 className="text-sm font-semibold text-gray-50 mt-2 mb-1">{children}</h5>,
          h6: ({ children }) => <h6 className="text-sm font-medium text-gray-300 mt-2 mb-1">{children}</h6>,
          p: ({ children }) => <p className="text-gray-300 leading-relaxed mb-3">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-inside text-gray-300 mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside text-gray-300 mb-3 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="text-gray-300">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary pl-4 my-3 text-gray-400 italic">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full border border-border-subtle rounded-lg">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-bg-elevated">{children}</thead>,
          th: ({ children }) => (
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-50 border-b border-border-subtle">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 text-sm text-gray-300 border-b border-border-subtle">
              {children}
            </td>
          ),
          strong: ({ children }) => <strong className="font-bold text-gray-50">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

/** Copy button for code blocks */
const CopyButton: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API not available
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity
                 bg-bg-surface hover:bg-bg-elevated border border-border-subtle rounded px-2 py-1
                 text-xs text-gray-400 hover:text-gray-50 cursor-pointer"
      aria-label="Copy code"
    >
      {copied ? '✓' : 'Copy'}
    </button>
  );
};

/** Extract text content from pre > code children for the copy button */
function extractCodeFromPre(children: React.ReactNode): string {
  if (!children) return '';
  if (typeof children === 'string') return children;
  if (React.isValidElement(children)) {
    const props = children.props as { children?: React.ReactNode };
    if (props.children) {
      if (typeof props.children === 'string') return props.children;
      if (Array.isArray(props.children)) {
        return props.children
          .map((child: React.ReactNode) => {
            if (typeof child === 'string') return child;
            if (React.isValidElement(child)) {
              const childProps = child.props as { children?: React.ReactNode };
              return typeof childProps.children === 'string' ? childProps.children : '';
            }
            return '';
          })
          .join('');
      }
    }
  }
  return '';
}

export default MarkdownRenderer;
